import { DisplayState } from '../../../api/gen/model/displayState';

/**
 * The LiveData keys each display state actually arrives with, so an operator pushing a state by hand
 * can reproduce what the engine sends instead of guessing key names.
 *
 * Guessing is the failure this exists to prevent: an unknown key is not rejected anywhere. It is
 * accepted, stored, pushed, and then ignored by every layer that was looking for a different name —
 * the template renders blank and nothing says why.
 *
 * **There are two vocabularies in play, and they are not interchangeable.**
 *
 * - Exit states published through `ExitDisplayStateMapper` use dotted, namespaced keys
 *   (`exit.plate`, `exit.amount`). These are the ones listed per state below.
 * - The parking engine's own dispatch path (`DisplayLiveDataKeys`) uses bare keys (`plate`, `fee`).
 *
 * So the same state can arrive with `exit.amount` or with `fee` depending on which code path pushed
 * it. A template built against one shows nothing when the other publishes.
 *
 * Sourced from `ExitDisplayStateMapper` and `ExitLiveDataKeys`/`EntryLiveDataKeys`/
 * `DisplayLiveDataKeys` on the backend.
 */

/** Keys the exit publisher sends, per state. States absent here send no live data of their own. */
const EXIT_STATE_KEYS: Partial<Record<DisplayState, string[]>> = {
    [DisplayState.ExitFree]: ['exit.plate', 'exit.info_kind', 'exit.valid_until', 'exit.balance', 'exit.currency'],
    [DisplayState.ExitViolations]: ['exit.plate'],
    [DisplayState.NoEntryRecord]: ['exit.plate'],
    [DisplayState.PaymentRequired]: [
        'exit.plate', 'exit.amount', 'exit.currency', 'exit.payment_method', 'exit.payment_qr_url',
    ],
    [DisplayState.PaymentInProgress]: ['exit.plate', 'exit.amount', 'exit.currency'],
    [DisplayState.PaymentDone]: ['exit.plate', 'exit.amount', 'exit.currency', 'exit.receipt_url', 'exit.receipt_qr'],
};

/** Keys the reversible-lane closed screens arrive with (backend `LaneLiveDataKeys`). */
const LANE_STATE_KEYS: Partial<Record<DisplayState, string[]>> = {
    [DisplayState.EntryClosed]: ['lane.opens_at'],
    [DisplayState.ExitClosed]: ['lane.opens_at'],
};

/** Keys the entry publisher sends. Offered on entry-side states. */
const ENTRY_KEYS = [
    'entry.plate', 'entry.organization', 'entry.zone', 'entry.warning_kind', 'entry.deny_reason',
    'entry.full_condition',
];

/** Bare keys the parking engine's own dispatch path sends. */
const ENGINE_KEYS = [
    'plate', 'fee', 'expiry', 'freeSpaces', 'violationSummary', 'customMessage', 'allowedEntries',
];

const ENTRY_STATES: DisplayState[] = [
    DisplayState.Idle, DisplayState.Scanning, DisplayState.DriveAuthorized, DisplayState.DriveUnlicensed,
    DisplayState.DriveWrongZone, DisplayState.DeniedNoRight, DisplayState.DeniedExpired,
    DisplayState.DeniedPoolFull, DisplayState.Full, DisplayState.DriveAuthorizedOverflowOrgPays,
    DisplayState.DriveAuthorizedOverflowParkerPays,
];

/**
 * Suggested keys for a state, most specific first: the exact set its publisher sends, then the
 * engine's bare keys, which any state can also arrive with.
 */
export function suggestedLiveDataKeys(state: DisplayState | null | undefined): string[] {
    if (!state) return [];

    const specific = LANE_STATE_KEYS[state] ?? EXIT_STATE_KEYS[state] ?? (ENTRY_STATES.includes(state) ? ENTRY_KEYS : []);
    return [...specific, ...ENGINE_KEYS.filter(k => !specific.includes(k))];
}
